import React, { useState } from 'react';
import styled from '@emotion/native';
import { TextInput } from 'react-native-paper';
import { BodyText } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import { green, white, mint, placeholder, red } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { TouchableOpacity } from 'react-native';
import { formatNoteTimestamp } from './helpers';

interface CreateNoteFormProps {
  onSaveNote: (content: string) => void;
  onCancel: () => void;
  isVisible: boolean;
  isLoading?: boolean;
}

const CreateNoteForm: React.FC<CreateNoteFormProps> = ({
  onSaveNote,
  onCancel,
  isVisible,
  isLoading = false,
}) => {
  const [noteContent, setNoteContent] = useState('');
  const { isDesktop } = useBreakpoint();

  const handleSaveNote = () => {
    if (noteContent.trim()) {
      onSaveNote(noteContent.trim());
      setNoteContent('');
    }
  };

  const handleCancel = () => {
    setNoteContent('');
    onCancel();
  };

  const canSave = noteContent.trim().length > 0;

  if (!isVisible) {
    return null;
  }

  return (
    <Container isDesktop={isDesktop}>
      <HeaderContainer>
        <TimestampText>{formatNoteTimestamp()}</TimestampText>
        <TouchableOpacity onPress={handleCancel}>
          <RemoveText>Remove</RemoveText>
        </TouchableOpacity>
      </HeaderContainer>

      <TextAreaContainer>
        <StyledTextArea
          value={noteContent}
          onChangeText={setNoteContent}
          placeholder="Write a reminder or recap — e.g. 'Client prefers neutral tones, interested in capsule wardrobe ideas.'"
          placeholderTextColor={placeholder}
          multiline
          textAlignVertical="top"
          contentStyle={textAreaContentStyle}
          disabled={isLoading}
          mode="flat"
          underlineStyle={hiddenUnderlineStyle}
          outlineStyle={hiddenOutlineStyle}
        />
      </TextAreaContainer>

      <ButtonContainer>
        <Button
          variant="tertiary"
          text="SAVE NOTE"
          onPress={handleSaveNote}
          disabled={!canSave || isLoading}
          loading={isLoading}
          customStyle={saveButtonStyle}
          customLabelStyle={saveButtonLabelStyle}
        />
      </ButtonContainer>
    </Container>
  );
};

export default CreateNoteForm;

const Container = styled.View<{ isDesktop: boolean }>(() => ({
  width: '100%',
  backgroundColor: white,
  borderRadius: 4,
  borderWidth: 1,
  borderColor: mint,
  padding: 0,
  overflow: 'hidden',
  marginBottom: 16,
}));

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: mint,
  paddingHorizontal: 24,
  paddingVertical: 8,
});

const TimestampText = styled(BodyText)({
  fontFamily: 'Lora',
  fontSize: 14,
  color: green,
  fontWeight: '500',
  letterSpacing: -0.14,
});

const RemoveText = styled(BodyText)({
  fontSize: 12,
  color: red,
  fontWeight: 'bold',
  textDecorationLine: 'underline',
});

const TextAreaContainer = styled.View({
  width: '100%',
  backgroundColor: white,
  paddingHorizontal: 8,
});

const StyledTextArea = styled(TextInput)({
  width: '100%',
  backgroundColor: white,
  borderWidth: 0,
  borderRadius: 0,
  minHeight: 160,
});

const textAreaContentStyle = {
  fontSize: 14,
  color: green,
};

const hiddenUnderlineStyle = {
  borderBottomWidth: 0,
  borderBottomColor: 'transparent',
  display: 'none' as const,
};

const hiddenOutlineStyle = {
  borderWidth: 0,
  borderColor: 'transparent',
};

const ButtonContainer = styled.View({
  alignItems: 'flex-end',
  padding: 16,
  backgroundColor: white,
});

const saveButtonStyle = {
  width: 140,
  height: 42,
  paddingHorizontal: 24,
  borderRadius: 120,
};

const saveButtonLabelStyle = {
  fontSize: 12,
  fontWeight: '700' as const,
  letterSpacing: 0.7,
};
